/**
 * ChatComposer
 *
 * Controlled textarea + send button used by all three chat surfaces.
 * Handles Enter-to-send (Shift+Enter = newline) and disabled state
 * while the assistant is responding.
 *
 * Props intentionally stay thin so consumers can wrap it without
 * fighting a complex API. Extra toolbar buttons (voice, file, stop)
 * can be injected via the `trailing` slot.
 */

import React from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type ChatComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Extra controls rendered to the right of the send button. */
  trailing?: React.ReactNode;
  /** Number of textarea rows (default 2). */
  rows?: number;
};

export function ChatComposer({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Schreiben Sie eine Nachricht…',
  trailing,
  rows = 2,
}: ChatComposerProps) {
  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSend(value.trim());
    }
  };

  return (
    <div className="flex gap-2 items-end">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className="flex-1 resize-none text-sm border border-border rounded-2xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-background disabled:opacity-50"
      />
      <Button
        onClick={() => { if (value.trim()) onSend(value.trim()); }}
        disabled={disabled || !value.trim()}
        className="bg-primary text-primary-foreground rounded-2xl h-[52px] w-[52px] p-0 shrink-0"
        aria-label="Senden"
      >
        <Send className="w-4 h-4" />
      </Button>
      {trailing}
    </div>
  );
}
